import { Link } from "react-router-dom";
import HeroCarousel from "./components/HeroCarousel";
import QuickEntries from "./components/QuickEntries";
import FeaturedWorks from "./components/FeaturedWorks";
import SectionHeading from "@/components/base/SectionHeading";
import { useSeo } from "@/hooks/useSeo";
import { getLatestNews } from "@/utils/media";
import { siteIntro } from "@/mocks/site";

export default function Home() {
  useSeo({
    title: "挣扎吧亚当君 - 影视作品介绍与剧情导航",
    description:
      "挣扎吧亚当君官方网站，提供电影和电视剧等影视作品介绍、剧情解析及相关资讯，帮助用户深入了解影视内容。",
    keywords: "挣扎吧亚当君,影视作品,剧情介绍,影视资讯,电影推荐,电视剧解析",
  });

  const latestNews = getLatestNews(3);

  const stats = [
    { value: "120+", label: "深度剧情导航" },
    { value: "480+", label: "影视作品库" },
    { value: "36", label: "行业观察专题" },
    { value: "5", label: "年持续更新" },
  ];

  return (
    <div className="w-full">
      <HeroCarousel />
      <QuickEntries />
      <FeaturedWorks />

      {/* 最新资讯预览 */}
      <section className="w-full px-4 md:px-8 py-14 md:py-16 bg-background-50 border-y border-background-200/70">
        <SectionHeading
          eyebrow="Latest News"
          title="最新影视资讯"
          desc="行业动态、深度解析与影视新闻，带你了解作品之外的故事。"
          moreTo="/news"
          moreLabel="进入资讯频道"
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 flex flex-col gap-4">
            {latestNews.map((item, i) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                className="group flex gap-4 items-start p-4 rounded-lg bg-background-100 hover:bg-accent-50 transition-colors cursor-pointer"
              >
                <span className="font-heading text-2xl font-black text-accent-500/70 w-8 shrink-0 leading-none mt-0.5">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-secondary-100 text-secondary-800">
                      {item.category}
                    </span>
                    <span className="text-xs text-foreground-500">{item.date}</span>
                  </div>
                  <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {item.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>

          <div className="lg:col-span-2">
            <Link
              to={`/news/${latestNews[0].id}`}
              className="group relative block w-full h-64 md:h-full md:min-h-[340px] rounded-lg overflow-hidden cursor-pointer bg-secondary-100"
            >
              <img
                src={latestNews[0].cover}
                alt={`影视资讯封面：${latestNews[0].title}`}
                title={`${latestNews[0].title} - 影视资讯`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
                <span className="inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium bg-accent-500 text-accent-950 mb-3">
                  头条 · {latestNews[0].category}
                </span>
                <h3 className="font-heading text-lg md:text-2xl font-bold text-background-50 leading-snug line-clamp-2">
                  {latestNews[0].title}
                </h3>
                <p className="mt-2 text-sm text-background-100/80 line-clamp-2 max-w-2xl">
                  {latestNews[0].summary}
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* 网站简介 */}
      <section className="w-full px-4 md:px-8 py-14 md:py-20">
        <div className="rounded-lg bg-primary-950 text-background-100 px-6 py-10 md:px-12 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-6 h-[3px] rounded-full bg-accent-500"></span>
                <span className="text-xs font-label font-medium tracking-[0.2em] text-accent-400 uppercase">
                  About Us
                </span>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
                深挖剧情，读懂挣扎
              </h2>
              <p className="text-sm md:text-base text-background-200/85 leading-relaxed">{siteIntro}</p>
              <Link
                to="/about"
                className="mt-7 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-accent-950 text-sm font-bold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                了解我们
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {stats.map((s) => (
                <div key={s.label} className="p-5 rounded-lg bg-background-50/5 border border-background-50/10">
                  <p className="font-heading text-2xl md:text-3xl font-black text-accent-400">{s.value}</p>
                  <p className="mt-1 text-xs md:text-sm text-background-200/80">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}